import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import fetchWithAuth from "../../lib/fetchWithAuth";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

interface Question {
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_option: string;
}

const EditQuestion: React.FC = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);

  const [formData, setFormData] = useState<Question>({
    question_text: "",
    option_a: "",
    option_b: "",
    option_c: "",
    option_d: "",
    correct_option: "",
  });

  const [contestId, setContestId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        const res = await fetchWithAuth(`${API_BASE_URL}/admin/questions/${questionId}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status} - ${text}`);
        }

        const data = await res.json();
        const found = data.question;

        if (!found) {
          await MySwal.fire("Not Found", "Question not found", "error");
          navigate("/admin/questions");
          return;
        }

        setFormData({
          question_text: found.question_text || "",
          option_a: found.option_a || "",
          option_b: found.option_b || "",
          option_c: found.option_c || "",
          option_d: found.option_d || "",
          correct_option: (found.correct_option || "").toLowerCase(),
        });

        setContestId(found.contest_id || null);
      } catch (err: any) {
        console.error("Error loading question:", err);
        MySwal.fire("Error", err.message || "Something went wrong", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const res = await fetchWithAuth(`${API_BASE_URL}/admin/questions/${questionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Failed to update question");

      await MySwal.fire("Success", "Question updated successfully", "success");

      if (contestId) {
        navigate(`admin/questions/${contestId}`);
      } else {
        navigate("/admin/questions");
      }
    } catch (err) {
      console.error("Update failed:", err);
      MySwal.fire("Error", "Failed to update question", "error");
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Edit Question</h1>

      {contestId && (
        <button
          onClick={() => navigate(`/admin/questions/${contestId}`)}
          className="mb-6 block text-indigo-600 hover:underline text-sm"
        >
          ← Back to View Questions
        </button>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 bg-white shadow p-6 rounded">
          <div>
            <label className="block text-sm font-medium">Question Text</label>
            <input
              type="text"
              name="question_text"
              value={formData.question_text}
              onChange={handleChange}
              className="w-full border p-2 rounded"
              required
            />
          </div>
          {["a", "b", "c", "d"].map((opt) => (
            <div key={opt}>
              <label className="block text-sm font-medium">{`Option ${opt.toUpperCase()}`}</label>
              <input
                type="text"
                name={`option_${opt}`}
                value={(formData as any)[`option_${opt}`]}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
              />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium">Correct Option</label>
            <select
              name="correct_option"
              value={formData.correct_option}
              onChange={handleChange}
              className="w-full border p-2 rounded"
              required
            >
              <option value="">Select</option>
              <option value="a">A</option>
              <option value="b">B</option>
              <option value="c">C</option>
              <option value="d">D</option>
            </select>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
            >
              💾 Save Changes
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EditQuestion;
